import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Theme from "./Theme.js";
import CompositeTextInput from "./CompositeTextInput.js";
/**
 * This TextInput field will allow you to install a function
 * to prohibit keys, validate the input, and to fire changes
 * only on a carriage return or blur.
 * @property {function} onChange 
 * @property {function} onfocus  
 * @property {function} validator 
 * @property {function} acceptKey 
 * @property {boolean} changeOnReturn 
 * @property {boolean} changeOnBlur 
 * @property {boolean} alwaysFireOnReturn 
 * @property {boolean} placeCaretAtEnd 
 * @property {boolean} focus 
 * @property {boolean} disabled 
 * @property {string} hint 
 * @property {string} placeholder 
 * @property {string} value 
 * @property {number} maxLength 
 * @property {number} size 
 **/
import { jsx as _jsx } from "react/jsx-runtime";
class TextInput extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    validator: PropTypes.func,
    acceptKey: PropTypes.func,
    changeOnReturn: PropTypes.bool,
    changeOnBlur: PropTypes.bool,
    alwaysFireOnReturn: PropTypes.bool,
    alwaysFireOnBlur: PropTypes.bool,
    placeCaretAtEnd: PropTypes.bool,
    focus: PropTypes.bool,
    disabled: PropTypes.bool,
    hint: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.string,
    maxLength: PropTypes.number,
    size: PropTypes.number
  };
  static defaultProps = {
    changeOnReturn: true,
    changeOnBlur: true,
    alwaysFireOnReturn: false,
    acceptKey: null,
    validator: null
  };
  constructor(props) {
    super(props);
    const {
      value
    } = this.props;
    const val = value || '';
    this.state = {
      showPassword: false,
      value: val,
      error: this.isInError(val)
    };
  }
  componentDidUpdate(prevProps) {
    const {
      value
    } = this.props;
    if (value !== prevProps.value) {
      this.setState({
        value,
        error: this.isInError(value)
      });
    }
  }
  componentDidMount() {
    const {
      focus
    } = this.props;
    if (focus && this.input) {
      setTimeout(() => this.input && this.input.focus(), 1);
    }
  }
  isInError(value) {
    const {
      validator
    } = this.props;
    if (validator) {
      return !validator(value);
    }
    return false;
  }
  onChange = e => {
    const error = this.isInError(e.target.value);
    const {
      changeOnReturn,
      alwaysFireOnReturn,
      onChange,
      eventData
    } = this.props;
    this.setState({
      value: e.target.value,
      error
    });
    if (changeOnReturn === false && alwaysFireOnReturn === false && onChange) {
      onChange({
        value: e.target.value,
        error,
        data: eventData
      });
    }
  };
  onFocus = e => {
    const {
      placeCaretAtEnd,
      onFocus
    } = this.props;
    if (placeCaretAtEnd) {
      const dom = ReactDOM.findDOMNode(this);
      setTimeout(() => {
        const index = dom.value ? dom.value.length : 0;
        dom.setSelectionRange(index, index);
      }, 0);
    }
    if (onFocus) {
      onFocus(e);
    }
  };
  onKeyPress = e => {
    const {
      alwaysFireOnReturn,
      onChange,
      changeOnReturn,
      value,
      eventData,
      allowError,
      acceptKey
    } = this.props;
    const {
      error
    } = this.state;
    const rtn = e.which === 13 || e.keyCode === 13;
    if (rtn && (changeOnReturn || alwaysFireOnReturn)) {
      if (onChange && alwaysFireOnReturn) {
        return onChange({
          event: e,
          isReturn: rtn,
          value: e.target.value,
          error,
          data: eventData
        });
      }
      if (e.target.value == value) {
        return true;
      }
      const err = this.isInError(e.target.value);
      if (err && allowError !== true) {
        return true;
      }
      if (onChange) {
        onChange({
          event: e,
          isReturn: rtn,
          value: e.target.value,
          error,
          data: eventData
        });
      }
      return true;
    }
    if (acceptKey && acceptKey(e) === false) {
      e.preventDefault();
      return false;
    }
    return true;
  };
  onBlur = e => {
    const val = e.target.value;
    const {
      value,
      onChange,
      allowError,
      changeOnBlur,
      alwaysFireOnBlur,
      eventData,
      onBlur
    } = this.props;
    const {
      error
    } = this.state;
    if (error) {
      // Return the value
      const err = this.isInError(val);
      if (allowError !== true && err) {
        // We are leaving the input. reset to last value if we dont allow errors
        e.target.value = value;
        this.setState({
          value,
          error: this.isInError(value)
        });
      } else {
        const same = alwaysFireOnBlur ? false : value == null ? val == null || val.trim() === '' : value === val;
        if (changeOnBlur && onChange && !same) {
          onChange({
            value: val,
            error: true,
            data: eventData,
            causedByBlur: true
          });
        }
      }
    } else {
      const same = alwaysFireOnBlur ? false : value == null ? val == null || val.trim() === '' : value === val;
      if (changeOnBlur && onChange && !same) {
        onChange({
          value: val,
          error: false,
          data: eventData,
          causedByBlur: true
        });
      }
    }
    if (onBlur && typeof onBlur === 'function') {
      onBlur(e);
    }
  };
  onToggleShowPassword = () => {
    const {
      showPassword
    } = this.state;
    this.setState({
      showPassword: !showPassword
    });
  };
  render() {
    const theme = this.context;
    /* eslint-disable no-unused-vars */
    const {
      className,
      variant,
      id,
      autoFocus,
      hint,
      placeholder,
      maxLength,
      size = '10',
      value,
      style,
      eventData,
      focus,
      placeCaretAtEnd,
      changeOnBlur,
      changeOnReturn,
      alwaysFireOnReturn,
      allowError,
      onChange,
      onKeyDown,
      onKeyPress,
      onFocus,
      onBlur,
      acceptKey,
      validator,
      password = false,
      disabled,
      layoutOptions,
      domRef,
      alwaysFireOnBlur,
      // don't want these going to the input by accident
      icon,
      button,
      pre,
      preLocation,
      post,
      postLocation,
      buttonVariant,
      buttonProps,
      inputClassName,
      buttonClassName,
      showPasswordIcon,
      hidePasswordIcon,
      type,
      error: pError,
      ...rest
    } = theme.props('TextInput', this.props);
    const {
      error,
      value: stateValue
    } = this.state;
    /* eslint-enable no-unused-vars */
    const cn = theme.classes('TextInput', 'textInput', variant, className);
    let err = false;
    if (pError != null) {
      err = !!pError;
    } else if (this.state == null || error == null) {
      err = this.isInError(value);
    } else {
      err = this.state && error;
    }
    const attr = err && {
      'data-error': err
    };
    if (icon || pre || post || button) {
      return /*#__PURE__*/_jsx(CompositeTextInput, {
        ...this.props
      });
    }
    const isPassword = password || type === 'password';
    let mType = type || 'text';
    if (isPassword && showPasswordIcon) {
      const {
        showPasswordIcon: x1,
        hidePasswordIcon: x2,
        ...prps
      } = this.props;
      const {
        showPassword
      } = this.state;
      mType = showPassword ? 'text' : 'password';
      return /*#__PURE__*/_jsx(CompositeTextInput, {
        ...prps,
        type: mType,
        buttonProps: {
          icon: showPassword ? hidePasswordIcon || showPasswordIcon : showPasswordIcon,
          onClick: this.onToggleShowPassword,
          type: 'button',
          title: showPassword ? 'Hide Password' : 'Show Password',
          eventData: 'showPassword'
        }
      });
    }
    return /*#__PURE__*/_jsx("input", {
      className: cn,
      id: id,
      type: type,
      disabled: disabled,
      autoFocus: autoFocus === true,
      ref: input => {
        this.input = input;
        if (domRef) domRef.current = input;
      },
      value: stateValue != null ? stateValue : '',
      placeholder: hint || '',
      maxLength: maxLength,
      size: size,
      style: style,
      ...attr,
      ...rest,
      onChange: this.onChange,
      onFocus: this.onFocus,
      onKeyDown: onKeyDown,
      onKeyPress: this.onKeyPress,
      onBlur: this.onBlur
    });
  }
}
export default TextInput;